import { H2, P, B, UL, LI, Callout, Table, Steps, Step } from "./ui";

export const FaltaMaterial = () => (
  <>
    <P>
      Es el aviso más frecuente: pulsas renderizar y el sistema responde que hace falta material
      que cubra toda la narración.
    </P>

    <H2 id="por-que">Por qué pasa</H2>
    <P>
      Para renderizar, <B>cada escena</B> tiene que tener material que cubra sus segundos de
      narración completos. No basta con que tenga clips: tienen que sumar la duración necesaria.
    </P>
    <P>
      La causa habitual es que los clips encontrados duran menos de lo previsto. Cada plano se
      recorta a un máximo y a lo que dure la fuente, así que un plano planificado para 15 segundos
      puede aportar solo 4 si el vídeo original es corto. Sumado a lo largo de una escena, deja un
      hueco.
    </P>

    <H2 id="solucion">Cómo se arregla</H2>
    <Steps>
      <Step n="1" title="Pulsa «Completar lo que falta»">
        Busca únicamente los segundos que faltan y conserva todo lo que ya estaba. No vuelve a
        pagar el material correcto.
      </Step>
      <Step n="2" title="Comprueba el aviso al terminar">
        Si aún queda algo sin cubrir, te dirá cuántos segundos exactos. Puedes pulsarlo otra vez:
        cada pasada acerca más.
      </Step>
      <Step n="3" title="Si se atasca, mira las escenas concretas">
        En la vista de material se ve qué escena está corta. A veces es más rápido subir un clip
        propio para ese hueco.
      </Step>
    </Steps>

    <Callout tipo="aviso" titulo="No uses «Regenerar todo» para esto">
      Tira el material correcto de todas las escenas y lo vuelve a pagar. «Completar lo que falta»
      hace justo lo que necesitas y cuesta una fracción.
    </Callout>

    <H2 id="cronico">Si pasa siempre</H2>
    <P>
      Que un vídeo se quede corto de material repetidamente suele significar que el tema tiene poco
      archivo visual. Revisa «Elegir un nicho»: es un problema de tema, no de configuración.
    </P>
  </>
);

export const EnCola = () => (
  <>
    <P>
      Pulsas renderizar y el vídeo queda en cola en vez de empezar.
    </P>

    <H2 id="por-que">Por qué</H2>
    <P>
      Se renderiza <B>un vídeo cada vez</B> en todo el sistema. Renderizar consume mucha máquina, y
      hacer dos a la vez haría que ambos tardaran más del doble. Cuando el que está delante termine,
      el tuyo arranca solo.
    </P>

    <H2 id="que-hacer">Qué hacer mientras</H2>
    <UL>
      <LI><B>Nada.</B> No hace falta que esperes en la pantalla; puedes cerrar el navegador.</LI>
      <LI>Puedes seguir trabajando en otros vídeos: escribir guiones, buscar material o revisar.</LI>
      <LI>No pulses renderizar otra vez: no adelanta el turno.</LI>
    </UL>

    <Callout tipo="info" titulo="Cuánto tarda un render">
      Depende de la duración del vídeo y de lo que haya por delante. Un vídeo de 20 minutos suele
      estar listo en unos 12-15 minutos de proceso, más la espera de la cola si la hay.
    </Callout>
  </>
);

export const SeParo = () => (
  <>
    <P>
      Una producción puede detenerse a mitad. Lo importante primero: <B>no pierdes lo que ya estaba
      hecho</B>.
    </P>

    <H2 id="causas">Por qué se para</H2>
    <Table
      head={["Causa", "Qué verás"]}
      rows={[
        ["Te quedaste sin créditos", "Aviso de saldo insuficiente"],
        ["Una fuente de material dejó de responder", "Escenas sin material, el resto bien"],
        ["Mantenimiento del servicio", "El proceso se corta sin mensaje claro"],
      ]}
    />

    <H2 id="retomar">Cómo retomarlo</H2>
    <Steps>
      <Step n="1" title="Recarga saldo si era eso">
        Desde tu cuenta, con una recarga o subiendo de plan.
      </Step>
      <Step n="2" title="Pulsa «Completar lo que falta»">
        Retoma la búsqueda donde se quedó. Las escenas ya cubiertas no se tocan ni se vuelven a pagar.
      </Step>
      <Step n="3" title="Revisa y renderiza">
        A partir de ahí, el flujo normal.
      </Step>
    </Steps>

    <Callout tipo="truco" titulo="Si el botón parece bloqueado">
      Cuando un proceso muere de forma brusca, el vídeo puede quedar unos minutos marcado como «en
      curso». Se desbloquea solo en cuanto el sistema detecta que ya no hay nadie trabajando en él.
      Espera un momento y vuelve a intentarlo.
    </Callout>
  </>
);

export const MaterialRaro = () => (
  <>
    <P>
      El vídeo se ha hecho, pero lo que se ve no pega con lo que se cuenta.
    </P>

    <H2 id="causas">De dónde viene</H2>
    <UL>
      <LI>
        <B>El tema tiene poco archivo.</B> La causa más habitual con diferencia. Si no existe imagen
        de lo que narras, se acaba tirando de material relacionado pero genérico.
      </LI>
      <LI>
        <B>El guion es abstracto.</B> «La ambición lo consumió» no tiene imagen posible; «vendió su
        casa para financiar la expedición» sí.
      </LI>
      <LI>
        <B>El título era vago</B> y arrastró a todo lo demás.
      </LI>
    </UL>

    <H2 id="arreglar">Arreglarlo en este vídeo</H2>
    <Steps>
      <Step n="1" title="Sustituye los clips concretos">
        En la vista de material, pulsa sobre los que fallan. Puedes buscar con tus propias palabras
        si las sugerencias no aciertan.
      </Step>
      <Step n="2" title="Sube material propio">
        Para los planos importantes que no encuentren nada bueno.
      </Step>
      <Step n="3" title="Vuelve a renderizar">
        Solo cuando el material te convenza. El vídeo anterior sigue disponible mientras tanto.
      </Step>
    </Steps>

    <H2 id="evitar">Evitarlo en los siguientes</H2>
    <UL>
      <LI>Títulos con nombres propios, lugares y fechas.</LI>
      <LI>Temas con registro visual: revisa la tabla de nichos en las guías.</LI>
      <LI>Guiones que cuenten hechos concretos en vez de valoraciones.</LI>
    </UL>
  </>
);

export const Otros = () => (
  <>
    <H2 id="voz">La voz no aparece en la lista</H2>
    <P>
      El catálogo se filtra por el idioma del canal. Si buscas una voz concreta y no sale, comprueba
      que el idioma del canal es el correcto. Si una voz configurada antes deja de estar disponible,
      se avisa y basta con elegir otra.
    </P>

    <H2 id="youtube">No puedo conectar YouTube</H2>
    <P>
      Hay que autorizar con la cuenta de Google que <B>administra</B> ese canal. Si gestionas
      varios, asegúrate de elegir la correcta en la pantalla de Google. Si sigue fallando,
      desconecta y vuelve a conectar.
    </P>

    <H2 id="subida">La subida a YouTube falla</H2>
    <P>
      Las causas más comunes son que la autorización caducó (desconecta y reconecta) o que el canal
      de YouTube tiene un límite diario de subidas alcanzado. El vídeo no se pierde: sigue
      disponible para descargar o reintentar.
    </P>

    <H2 id="miniatura">La miniatura no se aplica</H2>
    <P>
      YouTube exige tener la cuenta verificada para poner miniaturas personalizadas. Si no lo está,
      el vídeo se sube con un fotograma automático y podrás cambiar la miniatura desde YouTube una
      vez verifiques.
    </P>

    <H2 id="renders">Mis vídeos antiguos han desaparecido</H2>
    <P>
      Cada plan tiene un límite de almacenamiento y se conserva un número limitado de renders por
      proyecto. Los más antiguos se eliminan cuando entran nuevos. Si un vídeo te importa,
      descárgalo: la descarga no caduca.
    </P>

    <Callout tipo="info" titulo="¿Nada de esto lo resuelve?">
      Desde la aplicación puedes abrir un ticket de soporte. Incluye el nombre del vídeo y una
      captura: con eso se puede mirar exactamente qué pasó en tu caso.
    </Callout>
  </>
);
